/***
*
*   APP NAV
*   Primary navigation used inside the main app component
*
*   PROPS
*   items:       array of { label, icon, link? } or { section: 'LABEL' }
*   bottomItems: array of { label, icon, action? } – pinned to bottom
*
*   La accion principal del modulo activo (Registrar Vehiculo, Nueva Ruta…)
*   se pinta aqui arriba y la declara cada vista con usePrimaryAction.
*
**********/

import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { Icon } from 'components/lib';
import LogoTRS from 'components/logo/images/logo.png';
import { APP_VERSION } from 'version';
import { usePrimaryActionSlot } from './primaryAction';
import './app.scss';

function NavItem({ item, onClick, onMouseEnter, onMouseLeave }) {
  const inner = (
    <>
      {item.icon && <Icon className='icon' image={item.icon} size={16} strokeWidth={1.5} />}
      {item.label && <span className='label'>{item.label}</span>}
    </>
  );

  if (item.link) {
    return (
      <NavLink to={item.link} end={item.end} onClick={onClick}
        className={({ isActive }) => [item.danger && 'danger', isActive && 'active'].filter(Boolean).join(' ')}
        onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
        {inner}
      </NavLink>
    );
  }

  return (
    <div role='button' tabIndex={0} onClick={item.action}
      className={item.danger ? 'danger' : undefined}
      onKeyDown={e => e.key === 'Enter' && item.action?.()}
      onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
      {inner}
    </div>
  );
}

export function AppNav(props) {
  const [open, setOpen] = useState(false);
  const primaryAction = usePrimaryActionSlot();

  const closeOnMobile = () => setOpen(false);

  const renderItems = (items) => items?.map((item, i) => {
    if (item.section) {
      return (
        <div key={`s-${i}`} className='nav-section-label'>
          <span>{item.section}</span>
        </div>
      );
    }
    return <NavItem key={item.label} item={item} onClick={closeOnMobile} />;
  });

  return (
    <>
      <nav className={`sidebar popup ${open ? 'open' : ''}`}>

        {/* Mobile hamburger ──────────────────────── */}
        <button className='btn-togglenav' onClick={() => setOpen(!open)}
          aria-label={open ? 'Cerrar menú' : 'Abrir menú'}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="1.5" strokeLinecap="round">
            {open
              ? <><line x1="18" y1="6"  x2="6"  y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></>
              : <><line x1="3"  y1="6"  x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></>
            }
          </svg>
        </button>

        {/* Marca: logo circular + nombre y rol ───── */}
        <div className='nav-brand'>
          <img className='nav-brand-mark' src={LogoTRS} alt='Tu Ruta Segura' draggable={false} />
          <div className='nav-brand-copy'>
            <h1>Tu Ruta Segura</h1>
            <p>{props.roleLabel || 'Panel'}</p>
          </div>
        </div>

        {/* Accion principal del modulo ───────────── */}
        {primaryAction && (
          <button
            className='nav-cta'
            onClick={() => { primaryAction.onClick(); closeOnMobile(); }}
            title={primaryAction.label}
          >
            <span className='nav-cta-plus' aria-hidden="true">+</span>
            <span className='label'>{primaryAction.label}</span>
          </button>
        )}

        {/* Main nav links ────────────────────────── */}
        <section className='nav-links'>
          {renderItems(props.items)}
        </section>

        {/* Bottom items (logout, etc.) ───────────── */}
        {props.bottomItems?.length > 0 && (
          <section className='nav-bottom'>
            {renderItems(props.bottomItems)}
            {/* La version va aparte del rol: juntas se leian como una sola cosa */}
            <span className='nav-version'>Versión {APP_VERSION}</span>
          </section>
        )}

      </nav>
    </>
  );
}
